import { combineReducers } from 'redux'

import { Process }               from '../../api'
import { api }                   from '../../client'
import { bind, cleanse, unique } from '../../utils'
import createAction              from '../redux-utils/create-action'
import { Reducer }               from '../types'
import { createRequest }         from './communication.duck'
import { ApplicationState }      from './index'

const NAMESPACE = 'processes'

export const PROCESSES_BY_PROGRAM_ID_GET_SUCCESS = `[${NAMESPACE}] GET_BY_PROGRAM_ID_SUCCESS`
export const PROCESSES_BY_ID_GET_SUCCESS = `[${NAMESPACE}] GET_BY_ID_SUCCESS`
export const PROCESSES_DELETED = `[${NAMESPACE}] DELETED`
export const PROCESS_CONTRACT_ACCEPTED = `[${NAMESPACE}] PROCESS_CONTRACT_ACCEPTED`
export const PROCESS_EVALUATION_UPDATED = `[${NAMESPACE}] PROCESS_EVALUATION_UPDATED`
export const PROCESS_EVALUATION_ACCEPTED = `[${NAMESPACE}] PROCESS_EVALUATION_ACCEPTED`

interface ExtendedProcess extends Process {
  programId: number
}

interface ByIdState {
  [ id: number ]: ExtendedProcess
}

const byId: Reducer<ByIdState> = (state = {}, action) => {
  const { type, payload } = action

  switch (type) {
    case PROCESSES_BY_PROGRAM_ID_GET_SUCCESS:
    case PROCESSES_BY_ID_GET_SUCCESS:
      const { programId, processes } = payload

      return processes.reduce(
        (reduction, process) =>
          ({
            ...reduction,
            [ process.id ]: {
              ...process,
              programId
            },
          }),
        { ...state },
      )

    case PROCESSES_DELETED: {
      const { [ payload.id ]: deletedProcess, ...rest } = state

      return rest
    }

    case PROCESS_CONTRACT_ACCEPTED: {
      const { processId } = payload

      return {
        ...state,
        [ processId ]: {
          ...state[ processId ],
          isContractAccepted: true,
        },
      }
    }

    case PROCESS_EVALUATION_ACCEPTED: {
      const { processId } = payload

      return {
        ...state,
        [ processId ]: {
          ...state[ processId ],
          finalEvaluation: {
            ...state[ processId ].finalEvaluation,
            done: true,
          }
        }
      }
    }

    case PROCESS_EVALUATION_UPDATED: {
      const { processId, finalEvaluation } = payload

      return {
        ...state,
        [ processId ]: {
          ...state[ processId ],
          finalEvaluation,
        }
      }
    }

    default:
      return state
  }
}

type IdsState = number []
const ids: Reducer<IdsState> = (state = [], action) => {
  const { type, payload } = action

  switch (type) {
    case PROCESSES_BY_PROGRAM_ID_GET_SUCCESS:
    case PROCESSES_BY_ID_GET_SUCCESS: {
      const ids = (payload.processes as Process [])
        .map(({ id }) => id)

      return unique([
        ...state,
        ...ids,
      ])
    }

    default:
      return state
  }
}

interface ByProgramIdState {
  [ programId: number ]: number[]
}

const byProgramId: Reducer<ByProgramIdState> = (state = [], action) => {
  const { type, payload } = action

  switch (type) {
    case PROCESSES_BY_PROGRAM_ID_GET_SUCCESS: {
      const { processes, programId } = payload

      const ids = processes.map(({ id }) => id)

      return {
        ...state,
        [ programId ]: ids,
      }
    }

    default:
      return state
  }
}

export interface ProcessState {
  byProgramId: ByProgramIdState
  byId: ByIdState
  ids: IdsState
}

export default combineReducers({
  byProgramId,
  byId,
  ids,
})

const bindApi = bind(api)

const handleByProgramIdGetSuccess = (response, programId) =>
  createAction(
    PROCESSES_BY_PROGRAM_ID_GET_SUCCESS,
    {
      programId,
      processes: response.results,
    },
  )

const handleByIdGetSuccess = (response, programId) =>
  createAction(
    PROCESSES_BY_ID_GET_SUCCESS,
    {
      programId,
      processes: [ response ],
    },
  )

const genHandleProcessDeleted = id =>
  createAction(
    PROCESSES_DELETED,
    { id },
    {
      toast: {
        message: 'processDeleted',
      }
    }
  )

const handleProcessContractAccepted = (r, processId) =>
  createAction(
    PROCESS_CONTRACT_ACCEPTED,
    { processId },
    {
      toast: {
        message: 'contractAccepted',
      },
    },
  )

const genHandleFinalEvaluationAccepted = processId => () =>
  createAction(
    PROCESS_EVALUATION_ACCEPTED,
    { processId },
  )

const genHandleFinalEvaluationUpdated = processId => finalEvaluation =>
  createAction(
    PROCESS_EVALUATION_UPDATED,
    { processId, finalEvaluation },
    {
      toast: {
        message: 'finalEvaluationUpdated',
      },
    },
  )

export const processesGetRequest = createRequest({
  namespace:      'processes-by-program-id-get',
  request:        bindApi(api.getProcesses),
  successHandler: handleByProgramIdGetSuccess,
})

export const processGetRequest = createRequest({
  namespace:      'process-by-id-get',
  request:        bindApi(api.getProcess),
  successHandler: handleByIdGetSuccess,
})

export const genProcessesDeleteRequest = (id: number) => createRequest({
  namespace:      `processes-delete-id-${id}`,
  request:        bindApi(() => api.deleteProcess(id)),
  successHandler: genHandleProcessDeleted(id),
})

export const acceptProcessContractRequest = createRequest({
  namespace:      'process-contract-accept',
  request:        bindApi(api.acceptContract),
  successHandler: handleProcessContractAccepted,
})

export const genCompleteEvaluationRequest = processId => createRequest({
  namespace:      'process-final-evaluation-complete',
  request:        bindApi(api.completeEvaluation),
  successHandler: genHandleFinalEvaluationAccepted(processId),
})

export const genUpdateEvaluationRequest = processId => createRequest({
  namespace:      'process-final-evaluation-update',
  request:        bindApi(api.updateEvaluation),
  successHandler: genHandleFinalEvaluationUpdated(processId),
})

export const selectAllProcesses =
  ({ processes: { ids, byId } }: ApplicationState) =>
    cleanse(ids.map(id => byId[ id ]))

export const selectProcessesByProgramId =
  (programId: number) =>
    ({ processes: { byProgramId, byId } }: ApplicationState) =>
      byProgramId[ programId ]
        ? cleanse(byProgramId[ programId ].map(id => byId[ id ]))
        : []

export const selectProcessById =
  (id: number) =>
    ({ processes: { byId } }: ApplicationState): ExtendedProcess =>
      byId[ id ]



// WEBPACK FOOTER //
// ./src/redux/ducks/processes.duck.ts